<script>
// Media formats are now fully supported
const formatsToRemove = [];

export default {
  props: {
    inboxId: {
      type: Number,
      default: undefined,
    },
  },
  emits: ['onSelect'],
  data() {
    return {
      query: '',
      isLoading: false,
      syncError: null,
    };
  },
  computed: {
    whatsAppTemplateMessages() {
      // TODO: Remove the last filter when we support all formats
      const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      );

      if (!templates || templates.length === 0) {
        return [];
      }

      const approvedTemplates = templates.filter(
        template => template.status.toLowerCase() === 'approved'
      );

      return approvedTemplates.filter(template => {
        return template.components.every(component => {
          return !formatsToRemove.includes(component.format);
        });
      });
    },
    filteredTemplateMessages() {
      return this.whatsAppTemplateMessages.filter(template =>
        template.name.toLowerCase().includes(this.query.toLowerCase())
      );
    },
    hasTemplates() {
      return this.whatsAppTemplateMessages.length > 0;
    },
  },
  methods: {
    getTemplatebody(template) {
      const bodyComponent = template.components.find(
        component => component.type === 'BODY'
      );
      return bodyComponent ? bodyComponent.text : '';
    },
    async syncTemplates() {
      this.isLoading = true;
      this.syncError = null;

      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inboxId);
      } catch (error) {
        this.syncError = error.message || 'Failed to sync templates';
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="w-full">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center p-4">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-n-brand"
      />
      <span class="ml-2 text-sm text-n-slate-12">Syncing templates...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="syncError" class="p-4 bg-n-ruby-9/20 rounded-md">
      <p class="text-n-ruby-9 text-sm mb-2">{{ syncError }}</p>
      <button
        class="text-xs bg-n-ruby-9 text-white px-3 py-1 rounded hover:bg-n-ruby-10"
        @click="syncTemplates"
      >
        Retry Sync
      </button>
    </div>

    <!-- No Templates State -->
    <div v-else-if="!hasTemplates" class="p-4 text-center">
      <p class="text-n-slate-12 text-sm mb-2">No WhatsApp templates found</p>
      <button
        class="text-xs bg-n-brand text-white px-3 py-1 rounded hover:bg-n-brand-dark"
        @click="syncTemplates"
      >
        Sync Templates
      </button>
    </div>

    <!-- Templates List -->
    <div v-else>
      <div
        class="gap-1 bg-n-alpha-black2 items-center flex mb-2.5 py-0 px-2.5 rounded-lg outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus-within:outline-n-brand dark:focus-within:outline-n-brand"
      >
        <fluent-icon icon="search" class="text-n-slate-12" size="16" />
        <input
          v-model="query"
          type="search"
          :placeholder="$t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
          class="reset-base w-full h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
        />
      </div>
      <div
        class="bg-n-background outline-n-container outline outline-1 rounded-lg max-h-[18.75rem] overflow-y-auto p-2.5"
      >
        <div
          v-for="(template, i) in filteredTemplateMessages"
          :key="template.id"
        >
          <button
            class="rounded-lg cursor-pointer block p-2.5 text-left w-full hover:bg-n-alpha-2 dark:hover:bg-n-solid-2"
            @click="$emit('onSelect', template)"
          >
            <div>
              <div class="flex items-center justify-between mb-2.5">
                <p class="text-sm">
                  {{ template.name }}
                </p>
                <span
                  class="inline-block px-2 py-1 text-xs leading-none bg-n-slate-3 rounded-lg cursor-default text-n-slate-12"
                >
                  {{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.LANGUAGE') }} :
                  {{ template.language }}
                </span>
              </div>
              <div>
                <p class="font-medium">
                  {{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.TEMPLATE_BODY') }}
                </p>
                <p class="label-body">{{ getTemplatebody(template) }}</p>
              </div>
              <div class="mt-5">
                <p class="font-medium">
                  {{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.CATEGORY') }}
                </p>
                <p>{{ template.category }}</p>
              </div>
            </div>
          </button>
          <hr
            v-if="i != filteredTemplateMessages.length - 1"
            :key="`hr-${i}`"
            class="border-b border-solid border-n-weak my-2.5 mx-auto max-w-[95%]"
          />
        </div>
        <div v-if="!filteredTemplateMessages.length">
          <p>
            {{ $t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
            <strong>{{ query }}</strong>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.label-body {
  font-family: monospace;
}
</style>
